import { FC, ReactNode } from 'react';

import {
  ActivityIllustration,
  AiIllustration,
  AlertsIllustration,
  AnalyticsIllustration,
  ErrorIllustration,
  EventsIllustration,
  GalleryIllustration,
  GenericIllustration,
  GroupIllustration,
  NotFoundIllustration,
  OffersIllustration,
  OrdersIllustration,
  OrganizationsIllustration,
  PersonIllustration,
  PoiIllustration,
  QrIllustration,
  RentalsIllustration,
  ReportsIllustration,
  SearchIllustration,
  SetupIllustration,
  ShopIllustration,
  SpaIllustration,
  SurveysIllustration,
  UploadIllustration,
} from './illustrations';
import {
  EmptyActions,
  EmptyContainer,
  EmptyDescription,
  EmptyStateArtWrap,
  EmptyTitle,
  IllustrationWrapper,
} from './TableEmptyState.styled';

// Wariant ilustracji — dobierany do kontekstu tabeli.
export type TableEmptyStateVariant =
  | 'orders'
  | 'events'
  | 'surveys'
  | 'analytics'
  | 'activity'
  | 'shop'
  | 'rentals'
  | 'search'
  | 'offers'
  | 'alerts'
  | 'reports'
  | 'poi'
  | 'gallery'
  | 'person'
  | 'group'
  | 'setup'
  | 'spa'
  | 'ai'
  | 'organizations'
  | 'qr'
  | 'upload'
  | 'error'
  | 'notFound'
  | 'generic';

interface TableEmptyStateProps {
  /** Wariant animowanej ilustracji (domyślnie `generic`). */
  variant?: TableEmptyStateVariant;
  /** Nagłówek pustego stanu (np. „Brak nowych zamówień"). */
  title: string;
  /** Opcjonalny opis pod nagłówkiem. */
  description?: string;
  /** Opcjonalna akcja (np. przycisk) renderowana pod opisem. */
  action?: ReactNode;
  className?: string;
}

const illustrations: Record<TableEmptyStateVariant, FC> = {
  orders: OrdersIllustration,
  events: EventsIllustration,
  surveys: SurveysIllustration,
  analytics: AnalyticsIllustration,
  activity: ActivityIllustration,
  shop: ShopIllustration,
  rentals: RentalsIllustration,
  search: SearchIllustration,
  offers: OffersIllustration,
  alerts: AlertsIllustration,
  reports: ReportsIllustration,
  poi: PoiIllustration,
  gallery: GalleryIllustration,
  person: PersonIllustration,
  group: GroupIllustration,
  setup: SetupIllustration,
  spa: SpaIllustration,
  ai: AiIllustration,
  organizations: OrganizationsIllustration,
  qr: QrIllustration,
  upload: UploadIllustration,
  error: ErrorIllustration,
  notFound: NotFoundIllustration,
  generic: GenericIllustration,
};

/**
 * Animowany pusty stan dla tabel i kart. Łączy stonowaną, ruchomą ilustrację
 * line-art z nagłówkiem, opisem i opcjonalną akcją. Reaguje na motyw
 * (jasny/ciemny) oraz `prefers-reduced-motion`.
 */
export const TableEmptyState: FC<TableEmptyStateProps> = ({
  variant = 'generic',
  title,
  description,
  action,
  className,
}) => {
  const Illustration = illustrations[variant];

  return (
    <EmptyContainer className={className}>
      <IllustrationWrapper>
        <Illustration />
      </IllustrationWrapper>
      <EmptyTitle>{title}</EmptyTitle>
      {description && <EmptyDescription>{description}</EmptyDescription>}
      {action && <EmptyActions>{action}</EmptyActions>}
    </EmptyContainer>
  );
};

interface TableEmptyStateArtProps {
  /** Wariant ilustracji do wyrenderowania samodzielnie. */
  variant: TableEmptyStateVariant;
  /** Wysokość grafiki w px (domyślnie 96). */
  height?: number;
  className?: string;
}

/**
 * Samodzielna, animowana ilustracja danego wariantu — bez tytułu/opisu. Do użycia
 * np. w nagłówkach kreatorów (SetupWizard) zamiast statycznej ikony.
 */
export const TableEmptyStateArt: FC<TableEmptyStateArtProps> = ({
  variant,
  height = 96,
  className,
}) => {
  const Illustration = illustrations[variant];
  return (
    <EmptyStateArtWrap $height={height} className={className}>
      <Illustration />
    </EmptyStateArtWrap>
  );
};
